import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { godeskApi } from '../api';
import toast from 'react-hot-toast';
import { FiPlus, FiRefreshCw, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [panelDomain, setPanelDomain] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Edit State
  const [editingNotification, setEditingNotification] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editMessage, setEditMessage] = useState('');
  const [editPanelDomain, setEditPanelDomain] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchNotifications = async () => {
    try {
      const { data } = await godeskApi.getNotifications();
      setNotifications(data || []);
    } catch (err) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchNotifications(); }, []);

  const handleSend = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await godeskApi.sendNotification({ title, message, panel_domain: panelDomain });
      toast.success('Notification sent successfully');
      setTitle('');
      setMessage('');
      setPanelDomain('');
      fetchNotifications();
    } catch (err) {
      toast.error('Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  const handleStartEdit = (notification) => {
    setEditingNotification(notification);
    setEditTitle(notification.title);
    setEditMessage(notification.message);
    setEditPanelDomain(notification.panel_domain || '');
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await godeskApi.updateNotification(editingNotification.id, {
        title: editTitle,
        message: editMessage,
        panel_domain: editPanelDomain || null
      });
      toast.success('Notification updated successfully');
      setEditingNotification(null);
      fetchNotifications();
    } catch (err) {
      toast.error('Failed to update notification');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) return;
    try {
      toast.loading('Deleting notification...', { id: 'delete-notif' });
      await godeskApi.deleteNotification(id);
      toast.success('Notification deleted', { id: 'delete-notif' });
      fetchNotifications();
    } catch (err) {
      toast.error('Failed to delete notification', { id: 'delete-notif' });
    }
  };

  return (
    <DashboardLayout>
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">Broadcast Notifications</h1>
        <p className="text-muted-foreground text-sm mt-1">Send notifications to all child panels or a specific panel.</p>
      </div>

      <div className="card p-6 mb-6 max-w-3xl">
        <h3 className="text-lg font-semibold tracking-tight text-foreground mb-4">New Notification</h3>
        <form onSubmit={handleSend} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none text-foreground">Title</label>
            <input 
              type="text" 
              required
              value={title} 
              onChange={e => setTitle(e.target.value)} 
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50" 
              placeholder="e.g. System Maintenance" 
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none text-foreground">Target Panel (Optional)</label>
            <input 
              type="text" 
              value={panelDomain} 
              onChange={e => setPanelDomain(e.target.value)} 
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50" 
              placeholder="domain.com (Leave blank to broadcast to all)" 
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none text-foreground">Message</label>
            <textarea 
              required
              value={message} 
              onChange={e => setMessage(e.target.value)} 
              className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none" 
              rows="4"
              placeholder="Your message here..." 
            ></textarea>
          </div>
          <div className="flex justify-end pt-2">
            <button 
              type="submit" 
              disabled={sending} 
              className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
            >
              {sending ? 'Sending...' : 'Broadcast'}
            </button>
          </div>
        </form>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="overflow-x-auto min-h-[250px]">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead>
                <tr className="border-b border-border bg-muted/20">
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Date</th>
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Title</th>
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Target</th>
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground">Message</th>
                  <th className="h-10 px-4 align-middle font-medium text-muted-foreground text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="[&_tr:last-child]:border-0">
                {notifications.map(n => (
                  <tr key={n.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                    <td className="p-4 align-middle text-muted-foreground whitespace-nowrap">{new Date(n.created_at).toLocaleString()}</td>
                    <td className="p-4 align-middle font-medium text-foreground">{n.title}</td>
                    <td className="p-4 align-middle">
                      {n.panel_domain ? (
                        <span className="text-foreground font-medium">{n.panel_domain}</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border bg-secondary text-secondary-foreground border-transparent">All Panels</span>
                      )}
                    </td>
                    <td className="p-4 align-middle text-muted-foreground truncate max-w-xs">{n.message}</td>
                    <td className="p-4 align-middle text-right space-x-2">
                      <button 
                        onClick={() => handleStartEdit(n)} 
                        className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-all inline-flex"
                        title="Edit"
                      >
                        <FiEdit2 size={14} />
                      </button>
                      <button 
                        onClick={() => handleDelete(n.id)} 
                        className="p-1 text-destructive hover:bg-destructive/10 rounded-md transition-all inline-flex"
                        title="Delete"
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {notifications.length === 0 && (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-muted-foreground">
                      No notifications sent yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingNotification && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="card max-w-lg w-full p-6 shadow-lg relative animate-in fade-in zoom-in-95 duration-200">
            <button 
              onClick={() => setEditingNotification(null)}
              className="absolute right-4 top-4 p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-all"
            >
              <FiX size={16} />
            </button>
            <h3 className="text-lg font-semibold mb-4 text-foreground">Edit Notification</h3>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none text-foreground">Title</label>
                <input 
                  type="text" 
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" 
                  required
                  value={editTitle} 
                  onChange={e => setEditTitle(e.target.value)} 
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none text-foreground">Target Panel (Optional)</label>
                <input 
                  type="text" 
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" 
                  value={editPanelDomain} 
                  onChange={e => setEditPanelDomain(e.target.value)} 
                  placeholder="domain.com (Leave blank to broadcast to all)" 
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none text-foreground">Message</label>
                <textarea 
                  className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none" 
                  required
                  value={editMessage} 
                  onChange={e => setEditMessage(e.target.value)} 
                  rows="4"
                ></textarea>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button 
                  type="button"
                  onClick={() => setEditingNotification(null)}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={saving}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
